<script setup>
import { computed } from 'vue'
import { useGenresStore } from '@stores/genres'

const props = defineProps({
    genres: {
        type: Array,
        default: () => [],
    },
    hasTypeTitle: {
        type: Boolean,
        default: false,
    },
    horScroll: {
        type: Boolean,
        default: false,
    },
    horScrollMobile: {
        type: Boolean,
        default: false,
    },
    padded: {
        type: Boolean,
        default: false,
    },
})

const genresStore = useGenresStore()

// A name with no genre behind it still has to render: Router 4 throws on an
// undefined `slug`, and that takes the whole page down.
const extendedGenres = computed(() =>
    props.genres.map(genre => {
        const known = genresStore.genres.find(candidate => candidate.name === genre)
        return known || { name: genre, slug: genre }
    })
)
</script>

<template>
    <div
        :class="[
            'genres-list',
            horScroll && 'genres-list--hor-scroll',
            horScrollMobile && 'genres-list--hor-scroll-mobile',
            padded && 'genres-list--padded',
        ]"
    >
        <div
            v-for="(genre, index) in extendedGenres"
            :key="`genre${index}`"
            class="genres-list__item"
        >
            <router-link
                :to="{ name: 'genre', params: { slug: genre.slug } }"
                class="genres-list__genre"
            >
                {{ genre.name }}
            </router-link>

            <div
                v-if="hasTypeTitle"
                class="genres-list__type-title"
            >Genre</div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

%hor-scroll {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    height: 140px;
    overflow-x: auto;
    overflow-y: none;
    text-align: center;
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
        display: none;
    }
}

.genres-list {
    &--hor-scroll {
        @extend %hor-scroll;
    }

    &--hor-scroll-mobile {
        @extend %hor-scroll;

        @include desktop {
            display: block;
            height: auto;
            padding: unset;
            overflow-x: unset;
            overflow-y: unset;
            text-align: unset;
        }
    }

    &--padded {
        padding-left: $s-s * 2;
    }

    &__item {
        position: relative;
        display: inline-block;
        padding-right: $s-m;
        margin-bottom: $s-m;
    }

    &__genre {
        position: relative;
        display: block;
        padding: 0 $s-m;
        line-height: 44px;
        color: $c-text-secondary;
        cursor: pointer;

        &::before,
        &::after {
            @include fill;

            z-index: $layer-negative-z-index;
            content: '';
        }

        &::before {
            background: $c-surface-raised;
        }

        &::after {
            background: $c-text-tertiary;
            opacity: 0;

            @include transition(opacity, slow);
        }

        &:hover {
            text-decoration: none;
        }

        &:hover::after {
            opacity: 1;

            @include transition(opacity, base);
        }
    }

    &__type-title {
        @extend %contentTitleM;

        margin: $s-s 0;
        text-align: center;
    }
}
</style>
